import { isValidCpf, isValidMobilePhone } from '@brazilian-utils/brazilian-utils';
import * as Yup from 'yup';
import { isValidCnpj } from '../validators/cnpj-validator';
import { isExpirationDateExpired } from '../validators/expiration-date-validator';
import { isValidCreditCardNumber } from '../validators/credit-card-validator';
import { isAmexBrand, isValidCvv } from '../validators/cvv-validator';
import { errorMessages } from './input-error-messages';
import { regexPatterns } from './regex-patterns';

export function removeLeadingZero(str: string) {
  if (str.charAt(0) === '0') {
    return str.slice(1);
  }
  return str;
}

function testCpf(this: Yup.TestContext, value?: string) {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 11) {
    return this.createError({ message: errorMessages.cpfIncomplete });
  }
  return (
    isValidCpf(value ?? '') ||
    this.createError({ message: errorMessages.cpfInvalid })
  );
}

function testCnpj(this: Yup.TestContext, value?: string) {
  const digits = (value ?? '').replace(/[^A-Z0-9]/gi, '');
  if (digits.length < 14) {
    return this.createError({ message: errorMessages.cnpjIncomplete });
  }
  return (
    isValidCnpj(value ?? '') ||
    this.createError({ message: errorMessages.cnpjInvalid })
  );
}

function phoneSchema() {
  return Yup.string()
    .required(errorMessages.phoneRequired)
    .test(
      'phone-validation',
      errorMessages.phoneInvalid,
      function (value: string) {
        if (this.parent.countryCode !== '55') return true;
        const rawPhone = value.replace(/\D/g, '');
        return isValidMobilePhone(rawPhone, { version: 1 });
      }
    )
    .test(
      'phone-ninth-digit-required',
      errorMessages.phoneNinthDigitRequired,
      function (value: string) {
        if (this.parent.countryCode !== '55') return true;
        const rawPhone = value.replace(/\D/g, '');
        return rawPhone.charAt(2) === '9';
      }
    );
}

export const walletsInstallmentSchema = Yup.object().shape({
  installments: Yup.string().required(errorMessages.installmentsRequired),
});

export const customerInformationSchema = () => {
  return Yup.object().shape({
    email: Yup.string()
      .required(errorMessages.emailRequired)
      .matches(regexPatterns.email, errorMessages.emailInvalid),
    name: Yup.string()
      .required(errorMessages.nameRequired)
      .min(2, errorMessages.nameMin)
      .max(64, errorMessages.nameMax)
      .test(
        'no-only-spaces',
        errorMessages.nameRequired,
        (value) => !!value?.trim()
      )
      .test(
        'has-full-name',
        errorMessages.nameFullNameRequired,
        (value) => (value?.trim().split(/\s+/).filter(Boolean).length ?? 0) >= 2
      )
      .trim(),
    documentType: Yup.string().required(),
    cpf: Yup.string().when('documentType', {
      is: 'cpf',
      then: (schema) =>
        schema.required(errorMessages.cpfRequired).test('cpf-valid', testCpf),
      otherwise: (schema) => schema.notRequired(),
    }),
    cnpj: Yup.string().when('documentType', {
      is: 'cnpj',
      then: (schema) =>
        schema
          .required(errorMessages.cnpjRequired)
          .test('cnpj-valid', testCnpj),
      otherwise: (schema) => schema.notRequired(),
    }),
    passport: Yup.string().when('documentType', {
      is: 'passport',
      then: (schema) => schema.required(errorMessages.passportRequired),
      otherwise: (schema) => schema.notRequired(),
    }),
    countryCode: Yup.string().required(),
    phone: phoneSchema(),
  });
};

export const digitalWalletInformationSchema = Yup.object().shape({
  documentType: Yup.string().required(),
  cpf: Yup.string().when('documentType', {
    is: 'cpf',
    then: (schema) =>
      schema.required(errorMessages.cpfRequired).test('cpf-valid', testCpf),
    otherwise: (schema) => schema.notRequired(),
  }),
  cnpj: Yup.string().when('documentType', {
    is: 'cnpj',
    then: (schema) =>
      schema.required(errorMessages.cnpjRequired).test('cnpj-valid', testCnpj),
    otherwise: (schema) => schema.notRequired(),
  }),
  passport: Yup.string().when('documentType', {
    is: 'passport',
    then: (schema) => schema.required(errorMessages.passportRequired),
    otherwise: (schema) => schema.notRequired(),
  }),
  countryCode: Yup.string().required(),
  phone: phoneSchema(),
  installments: Yup.string().nullable().optional(),
});

export const billingAddressSchema = Yup.object().shape({
  'billing-address-street': Yup.string()
    .required(errorMessages.streetRequired)
    .min(2, errorMessages.stateRequired)
    .max(64, errorMessages.streetMaxLength),
  'billing-address-city': Yup.string()
    .required(errorMessages.cityRequired)
    .min(2, errorMessages.cityRequired),
  'billing-address-country': Yup.string().required().min(2).max(2),
  'billing-address-district': Yup.string()
    .required(errorMessages.districtRequired)
    .min(2, errorMessages.districtRequired)
    .max(64, errorMessages.districtMaxLength),
  'billing-address-complement': Yup.string(),
  'billing-address-zip-code': Yup.string().when('billing-address-country', {
    is: 'BR',
    then: (schema) => {
      return schema
        .required(errorMessages.cepRequired)
        .min(9, errorMessages.cepInvalid)
        .max(9, errorMessages.cepInvalid);
    },
    otherwise: (schema) => {
      return schema.required(errorMessages.cepRequired);
    },
  }),
  'billing-address-state': Yup.string().required(errorMessages.stateRequired),
  'billing-address-without-number': Yup.boolean().optional(),
  'billing-address-number': Yup.string().when(
    'billing-address-without-number',
    {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => {
        return schema.required(errorMessages.numberRequired);
      },
    }
  ),
});

export const shippingAddressSchema = Yup.object().shape({
  'shipping-address-street': Yup.string()
    .required(errorMessages.streetRequired)
    .min(2, errorMessages.stateRequired)
    .max(64, errorMessages.streetMaxLength),
  'shipping-address-city': Yup.string()
    .required(errorMessages.cityRequired)
    .min(2, errorMessages.cityRequired),
  'shipping-address-country': Yup.string().required().min(2).max(2),
  'shipping-address-district': Yup.string()
    .required(errorMessages.districtRequired)
    .min(2, errorMessages.districtRequired)
    .max(64, errorMessages.districtMaxLength),
  'shipping-address-complement': Yup.string(),
  'shipping-address-zip-code': Yup.string().when('shipping-address-country', {
    is: 'BR',
    then: (schema) => {
      return schema
        .required(errorMessages.cepRequired)
        .min(9, errorMessages.cepInvalid)
        .max(9, errorMessages.cepInvalid);
    },
    otherwise: (schema) => {
      return schema.required(errorMessages.cepRequired);
    },
  }),
  'shipping-address-state': Yup.string().required(errorMessages.stateRequired),
  'shipping-address-without-number': Yup.boolean().optional(),
  'shipping-address-number': Yup.string().when(
    'shipping-address-without-number',
    {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => {
        return schema.required(errorMessages.numberRequired);
      },
    }
  ),
});

export const billingInformationSchema = ({
  isCreditCard,
  isRecurrencePayment = false,
  brand,
}: {
  isCreditCard: boolean;
  isRecurrencePayment?: boolean;
  brand?: string;
}) => {
  if (isCreditCard) {
    return Yup.object().shape({
      ...billingAddressSchema.fields,
      'allow-recurrence': isRecurrencePayment
        ? Yup.boolean().oneOf(
            [true],
            errorMessages.recurrenceAcceptanceRequired
          )
        : Yup.boolean().optional(),
      creditCard: Yup.string()
        .required(errorMessages.creditCardRequired)
        .test('credit-card-valid', errorMessages.creditCardInvalid, (value) =>
          isValidCreditCardNumber(value ?? '')
        ),
      cardHolder: Yup.string().required(errorMessages.cardHolderRequired),
      expirationDate: Yup.string()
        .matches(
          regexPatterns.expirationDate,
          errorMessages.expirationDateInvalid
        )
        .required(errorMessages.expirationDateRequired)
        .test(
          'expiration-not-expired',
          errorMessages.expirationDateExpired,
          (value) => !!value && !isExpirationDateExpired(value)
        ),
      cvv: Yup.string()
        .required(errorMessages.cvvRequired)
        .test('cvv-valid', function (value) {
          if (isValidCvv(value ?? '', brand)) return true;
          return this.createError({
            message: isAmexBrand(brand)
              ? errorMessages.cvvInvalidAmex
              : errorMessages.cvvInvalid,
          });
        }),
      installments: Yup.string().required(errorMessages.installmentsRequired),
    });
  }

  return Yup.object().shape({
    ...billingAddressSchema.fields,
    'allow-recurrence': Yup.boolean().optional(),
    creditCard: Yup.string().optional(),
    cardHolder: Yup.string().optional(),
    expirationDate: Yup.string().optional(),
    cvv: Yup.string().optional(),
    installments: Yup.string().optional(),
  });
};
